import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { createSelector } from "reselect";
import { getUserData } from "../slices/thunk";
import { useTranslation } from "react-i18next";
import { toProperCase } from "./Hooks/UserHooks";
import { openLoganSupportTicket } from "features/productAssistant/events";

interface JwtPayload {
  userType: string;
  firstName?: string;
  lastName?: string;
}

const getInitials = (name?: string, firstName?: string, lastName?: string) => {
  const fullName = name && name !== "Admin" ? name : [firstName, lastName].filter(Boolean).join(" ");
  const initials = fullName
    ?.trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return initials || "U";
};

const getStoredAuthToken = () => {
  const authUser = localStorage.getItem("authUser");

  if (!authUser) return null;

  try {
    return JSON.parse(authUser);
  } catch (error) {
    return authUser.replace(/^"|"$/g, "");
  }
};

const getRelibuildFrontendUrl = () => {
  const configuredUrl = process.env.REACT_APP_RELIBUILD_FRONTEND_URL;
  const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

  return configuredUrl || (isLocalhost ? "http://localhost:5173" : "https://www.relibuild.com");
};

const ProfileDropdown = () => {
  const dispatch: any = useDispatch();

  const token = getStoredAuthToken();
  let decoded: any = "";
  if (token !== null) {
    decoded = jwtDecode<JwtPayload>(token);
  } else {
    console.error("Token is null");
  }
  const [userName, setUserName] = useState<any>("Admin");
  const [userType, setUserType] = useState<any>("Admin");
  const [profilePic, setProfilePic] = useState<any>(null);
  const [profilePicError, setProfilePicError] = useState(false);

  const selectUser = createSelector(
    (state: any) => state.Profile.user,
    (user) => user
  );

  const user = useSelector(selectUser);

  useEffect(() => {
    if (decoded?.userType) {
      setUserType(decoded.userType);
    }
  }, [decoded?.userType, userName, user]);
  useEffect(() => {
    dispatch(getUserData()).then((resp: any) => {
      setUserName(resp && resp[0].user_first_name);
      setProfilePic(resp && resp[0].ua_profile_pic);
      setProfilePicError(false);
    });
  });
  const {t} = useTranslation()
  console.log("profile",profilePic)
  const showProfileImage = Boolean(profilePic) && !profilePicError;
  const initials = getInitials(userName, decoded?.firstName, decoded?.lastName);
  const canReturnToRelibuild =
    decoded?.userType === "homeowner" ||
    decoded?.userType === "real_estate_agent";

  const handleBackToRelibuild = () => {
    if (!token) return;

    const url = new URL(getRelibuildFrontendUrl());
    url.searchParams.set("token", token);
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  return (
    <React.Fragment>
    <Dropdown className="ms-sm-2 header-item topbar-user modern-profile-dropdown">
        <Dropdown.Toggle
            type="button"
            className="btn bg-transparent border-0 arrow-none"
            id="page-header-user-dropdown"
            aria-label={t("Open profile menu")}
        >
            <span className="d-flex align-items-center">
                {showProfileImage ? (
                <img
                    className="rounded-circle header-profile-user"
                    src={profilePic}
                    alt={t("Header Avatar")}
                    onError={() => setProfilePicError(true)}
                />
                ) : (
                  <span
                    className="rounded-circle header-profile-user d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary fw-semibold"
                    aria-label={t("Header Avatar")}
                  >
                    {initials}
                  </span>
                )}
                <span className="text-start ms-xl-2">
                    <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text">
                        {userName}
                    </span>
                    <span className="d-none d-xl-block ms-1 fs-sm text-muted user-name-sub-text">
                        {toProperCase( userType.replace("_"," ").replace("_"," "))}
                    </span>
                </span>
            </span>
        </Dropdown.Toggle>
        <Dropdown.Menu className="dropdown-menu-end modern-dropdown modern-profile-menu">
            <h6 className="dropdown-header">{t("Welcome")} {userName}!</h6>
            {canReturnToRelibuild && (
              <Dropdown.Item onClick={handleBackToRelibuild}>
                  <i className="mdi mdi-arrow-left-circle-outline text-muted fs-lg align-middle me-1"></i>{" "}
                  <span className="align-middle">Back to RELiBUILD</span>
              </Dropdown.Item>
            )}
            <Dropdown.Item href={process.env.PUBLIC_URL + "/profile"}>
                <i className="mdi mdi-account-circle-outline text-muted fs-lg align-middle me-1"></i>{" "}
                <span className="align-middle">My Profile</span>
            </Dropdown.Item>
            <Dropdown.Item onClick={()=>{
              
            }} href={process.env.PUBLIC_URL + "/apps-chat"}>
                <i className="mdi mdi-message-text-outline text-muted fs-lg align-middle me-1"></i>{" "}
                <span className="align-middle">{t("Messages")}</span>
            </Dropdown.Item>
            <Dropdown.Item onClick={openLoganSupportTicket}>
                <i className="mdi mdi-lifebuoy text-muted fs-lg align-middle me-1"></i>{" "}
                <span className="align-middle">{t("Help")}</span>
            </Dropdown.Item>
            <div className="dropdown-divider"></div>
            <Dropdown.Item href={process.env.PUBLIC_URL + "/pages-profile-settings"}>
       
                <i className="mdi mdi-cog-outline text-muted fs-lg align-middle me-1"></i>{" "}
                <span className="align-middle">{t("Settings")}</span>
            </Dropdown.Item>
            <Dropdown.Item href={process.env.PUBLIC_URL + "/logout"}>
                <i className="mdi mdi-logout text-muted fs-lg align-middle me-1"></i>{" "}
                <span className="align-middle" data-key="t-logout">
                    {t("Logout")}
                </span>
            </Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
</React.Fragment>

  );
};

export default ProfileDropdown;
